/**
 * Invitation Tracking API Client
 *
 * Service layer for invitation tracking CRUD and bulk operations.
 * Routes through Railway backend for JWT authentication.
 *
 * SECURITY: All requests require authentication via HttpOnly cookies.
 *
 * Backend endpoints (from backend/routers/invitation_tracking.py):
 * - POST   /api/invitation-tracking/invitations          - Create invitation
 * - GET    /api/invitation-tracking/invitations          - List invitations (paginated, filtered)
 * - GET    /api/invitation-tracking/invitations/{id}     - Get invitation detail with events
 * - PATCH  /api/invitation-tracking/invitations/{id}/status - Update invitation status
 * - DELETE /api/invitation-tracking/invitations/{id}     - Delete invitation
 * - POST   /api/invitation-tracking/invitations/bulk     - Bulk create invitations
 * - POST   /api/invitation-tracking/invitations/bulk-resend  - Bulk resend invitations
 * - POST   /api/invitation-tracking/invitations/bulk-remind  - Bulk send reminders
 * - GET    /api/invitation-tracking/stats                - Get invitation statistics
 */

import { apiClient, handleApiError } from '@shared/services/apiClient';
import type {
  InvitationRecord,
  InvitationDetailResponse,
  InvitationListResponse,
  InvitationCreateRequest,
  InvitationFilter,
  InvitationStatus,
  InvitationStats,
  InvitationAnalytics,
  BulkOperationResponse,
  RSVPRequest,
  RSVPResponse,
  EventType,
} from '@shared/types/invitationTracking';

/**
 * API base path for invitation tracking endpoints
 */
const BASE_PATH = '/invitation-tracking';

/**
 * Convert an InvitationFilter object to URLSearchParams-compatible query params.
 * Strips undefined/null values so they are not sent to the backend.
 */
function buildFilterParams(filters?: InvitationFilter): Record<string, string> {
  if (!filters) return {};

  const params: Record<string, string> = {};

  if (filters.event_type) params.event_type = filters.event_type;
  if (filters.invitation_type) params.invitation_type = filters.invitation_type;
  if (filters.status) params.status = filters.status;
  if (filters.search) params.search = filters.search;
  if (filters.date_from) params.date_from = filters.date_from;
  if (filters.date_to) params.date_to = filters.date_to;
  if (filters.sort_by) params.sort_by = filters.sort_by;
  if (filters.sort_order) params.sort_order = filters.sort_order;
  if (filters.page !== undefined) params.page = String(filters.page);
  if (filters.per_page !== undefined) params.per_page = String(filters.per_page);

  return params;
}

/**
 * Invitation Tracking API client
 *
 * Follows the object-literal pattern used by emailInvitationApi.
 */
export const invitationTrackingApi = {
  // ============================================================
  // SINGLE INVITATION METHODS
  // ============================================================

  /**
   * Create a new invitation record
   */
  async createInvitation(data: InvitationCreateRequest): Promise<InvitationRecord> {
    try {
      const response = await apiClient.post<InvitationRecord>(
        `${BASE_PATH}/invitations`,
        data
      );
      return response.data;
    } catch (error) {
      console.error('[InvitationTrackingApi] Create invitation error:', error);
      throw new Error(handleApiError(error));
    }
  },

  /**
   * List invitations with optional filters and pagination
   */
  async listInvitations(filters?: InvitationFilter): Promise<InvitationListResponse> {
    try {
      const params = buildFilterParams(filters);
      const response = await apiClient.get<InvitationListResponse>(
        `${BASE_PATH}/invitations`,
        { params }
      );
      return response.data;
    } catch (error) {
      console.error('[InvitationTrackingApi] List invitations error:', error);
      throw new Error(handleApiError(error));
    }
  },

  /**
   * Get a single invitation by ID, including its lifecycle events
   */
  async getInvitation(id: number): Promise<InvitationDetailResponse> {
    try {
      const response = await apiClient.get<InvitationDetailResponse>(
        `${BASE_PATH}/invitations/${id}`
      );
      return response.data;
    } catch (error) {
      console.error(`[InvitationTrackingApi] Get invitation ${id} error:`, error);
      throw new Error(handleApiError(error));
    }
  },

  /**
   * Update the status of an invitation
   */
  async updateStatus(id: number, status: InvitationStatus): Promise<InvitationRecord> {
    try {
      const response = await apiClient.patch<InvitationRecord>(
        `${BASE_PATH}/invitations/${id}/status`,
        { status }
      );
      return response.data;
    } catch (error) {
      console.error(`[InvitationTrackingApi] Update status for ${id} error:`, error);
      throw new Error(handleApiError(error));
    }
  },

  /**
   * Delete an invitation
   */
  async deleteInvitation(id: number): Promise<void> {
    try {
      await apiClient.delete(`${BASE_PATH}/invitations/${id}`);
    } catch (error) {
      console.error(`[InvitationTrackingApi] Delete invitation ${id} error:`, error);
      throw new Error(handleApiError(error));
    }
  },

  // ============================================================
  // BULK METHODS
  // ============================================================

  /**
   * Bulk create multiple invitations in one request
   */
  async bulkCreate(invitations: InvitationCreateRequest[]): Promise<InvitationRecord[]> {
    try {
      const response = await apiClient.post<InvitationRecord[]>(
        `${BASE_PATH}/invitations/bulk`,
        { invitations }
      );
      return response.data;
    } catch (error) {
      console.error('[InvitationTrackingApi] Bulk create error:', error);
      throw new Error(handleApiError(error));
    }
  },

  /**
   * Bulk resend invitations by their IDs
   */
  async bulkResend(invitationIds: number[]): Promise<BulkOperationResponse> {
    try {
      const response = await apiClient.post<BulkOperationResponse>(
        `${BASE_PATH}/invitations/bulk-resend`,
        { invitation_ids: invitationIds }
      );
      return response.data;
    } catch (error) {
      console.error('[InvitationTrackingApi] Bulk resend error:', error);
      throw new Error(handleApiError(error));
    }
  },

  /**
   * Bulk send reminders for selected invitations
   */
  async bulkRemind(
    invitationIds: number[],
    reminderTemplate: string
  ): Promise<BulkOperationResponse> {
    try {
      const response = await apiClient.post<BulkOperationResponse>(
        `${BASE_PATH}/invitations/bulk-remind`,
        { invitation_ids: invitationIds, reminder_template: reminderTemplate }
      );
      return response.data;
    } catch (error) {
      console.error('[InvitationTrackingApi] Bulk remind error:', error);
      throw new Error(handleApiError(error));
    }
  },

  // ============================================================
  // RSVP
  // ============================================================

  /**
   * Record an RSVP response for an invitation
   */
  async recordRSVP(invitationId: number, data: RSVPRequest): Promise<RSVPResponse> {
    try {
      const response = await apiClient.post<RSVPResponse>(
        `${BASE_PATH}/invitations/${invitationId}/rsvp`,
        data
      );
      return response.data;
    } catch (error) {
      console.error(`[InvitationTrackingApi] Record RSVP for ${invitationId} error:`, error);
      throw new Error(handleApiError(error));
    }
  },

  // ============================================================
  // EXPORT
  // ============================================================

  /**
   * Export filtered invitations as CSV download
   */
  async exportInvitations(filters?: InvitationFilter): Promise<Blob> {
    try {
      const params = buildFilterParams(filters);
      const response = await apiClient.get(`${BASE_PATH}/invitations/export`, {
        params,
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      console.error('[InvitationTrackingApi] Export invitations error:', error);
      throw new Error(handleApiError(error));
    }
  },

  // ============================================================
  // STATS & ANALYTICS
  // ============================================================

  /**
   * Get aggregated invitation statistics, optionally filtered by event type
   */
  async getStats(eventType?: EventType): Promise<InvitationStats> {
    try {
      const params: Record<string, string> = {};
      if (eventType) params.event_type = eventType;

      const response = await apiClient.get<InvitationStats>(
        `${BASE_PATH}/stats`,
        { params }
      );
      return response.data;
    } catch (error) {
      console.error('[InvitationTrackingApi] Get stats error:', error);
      throw new Error(handleApiError(error));
    }
  },

  /**
   * Get comprehensive invitation analytics with delivery rates and RSVP breakdown
   */
  async getAnalytics(eventType?: EventType): Promise<InvitationAnalytics> {
    try {
      const params: Record<string, string> = {};
      if (eventType) params.event_type = eventType;

      const response = await apiClient.get<InvitationAnalytics>(
        `${BASE_PATH}/analytics`,
        { params }
      );
      return response.data;
    } catch (error) {
      console.error('[InvitationTrackingApi] Get analytics error:', error);
      throw new Error(handleApiError(error));
    }
  },
};
